import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { AuthService } from '../Service/app-service';
import { ToastrService } from 'ngx-toastr';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import * as XLSX from 'xlsx';
import { SharedService } from '../Service/shared.service';

@Component({
  selector: 'app-add-notification',
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, CommonModule, NgxSpinnerModule, RouterModule],
  templateUrl: './add-notification.component.html',
  styleUrl: './add-notification.component.css'
})
export class AddNotificationComponent {
  notificationForm!: FormGroup;
  activeTab: any = 'ONE_TIME';
  submitted = false;
  readonly allDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  selectedDays: string[] = [];
  daysError = false;
  selectedTrigger: any = null;
  triggerError = false;
  mediaFile: File | null = null;
  toastMessage = '';
  toastVisible = false;
  tagsData: any = [];
  showPlayistDiv: boolean = false;
  showGenreDiv: boolean = false;
  showCustomUploadDiv: boolean = false;
  editMode: boolean = false
  imagePreviewUrl: string | null = null;
  excelFile: File | null = null;
  notificationId: any;
  notificationData: any;
  currentPage: number = 1;
  totalEntries: number = 0;
  totalPages: number = 0;
  page = 0;
  size = 15;
  today: string = new Date().toISOString().split('T')[0]
  ExcelFileName: any;
  fileSizeError: boolean = false;

  constructor(private fb: FormBuilder, private authService: AuthService, private tosterService: ToastrService, private spinner: NgxSpinnerService, private route: ActivatedRoute, private router: Router, private sharedService: SharedService) { }

  ngOnInit(): void {
    this.notificationForm = this.fb.group({
      title: ['', [Validators.required, Validators.minLength(3), Validators.maxLength(50)]],
      type: ['', Validators.required],
      audience: ['', Validators.required],
      module: ['', Validators.required],
      message: ['', [Validators.required, Validators.minLength(10), Validators.maxLength(500)]],
      subModule: [''],
      oneTimeDate: [''],
      oneTimeTime: [''],
      weeklyTime: [''],
      weeklyDate: [''],
    });
    this.activeTab = 'ONE_TIME'
    this.switchTab('ONE_TIME')
    this.getTagsData()
    this.patchFormsDetails()

  }

  // ─── Control Getters ───────────────────────────────────
  get title() { return this.notificationForm.get('title')!; }
  get type() { return this.notificationForm.get('type')!; }
  get audience() { return this.notificationForm.get('audience')!; }
  get module() { return this.notificationForm.get('module')!; }
  get subModule() { return this.notificationForm.get('subModule')!; }
  get message() { return this.notificationForm.get('message')!; }
  get oneTimeDate() { return this.notificationForm.get('oneTimeDate')!; }
  get oneTimeTime() { return this.notificationForm.get('oneTimeTime')!; }
  get weeklyTime() { return this.notificationForm.get('weeklyTime')!; }
  get weeklyDate() { return this.notificationForm.get('weeklyDate')!; }


  switchTab(tab: any): void {
    this.activeTab = tab;
    this.submitted = false;
    this.daysError = false;
    this.triggerError = false;
    this.oneTimeDate.updateValueAndValidity();
  }


  toggleDay(day: string): void {
    if (this.selectedDays.includes(day)) {
      this.selectedDays = this.selectedDays.filter(d => d !== day);
    } else {
      this.selectedDays.push(day);
    }

    this.daysError = this.selectedDays.length === 0;
  }

  isDaySelected(day: string): boolean {
    return this.selectedDays.includes(day);
  }

  // ─── Trigger ───────────────────────────────────────────
  selectTrigger(trigger: any): void {
    this.selectedTrigger = trigger;
    this.triggerError = false;
  }

  // ─── Media ─────────────────────────────────────────────
  onMediaChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.[0]) {
      const file = input.files[0];
      const size = (file.size / 1024) / 1024;
      if (size > 5) {
        this.fileSizeError = true;
        this.mediaFile = null;
        this.imagePreviewUrl = null;
        this.tosterService.error("File size should not exceed 5 MB");
        return;
      }
      this.fileSizeError = false;
      this.mediaFile = file;

      const reader = new FileReader();
      reader.onload = () => {
        this.imagePreviewUrl = reader.result as string;
      };
      reader.readAsDataURL(file);
    }
  }

  // ─── Schedule Hint ─────────────────────────────────────
  get scheduleHint(): { text: string; type: 'success' | 'info' | 'warn' } {
    if (this.activeTab === 'ONE_TIME') {
      const d = this.oneTimeDate.value, t = this.oneTimeTime.value;
      if (d && t) {
        const date = new Date(`${d}T${t}`);
        const fmt = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
          + ' at ' + date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        return { text: `✅ Scheduled: ${fmt}`, type: 'success' };
      }
      return { text: 'ONE_TIME — pick a date to continue', type: 'info' };
    }

    if (this.activeTab === 'WEEKLY_RECURRING') {
      if (this.selectedDays.length && this.weeklyTime.value)
        return { text: `Every ${this.selectedDays.join(', ')} at ${this.fmt12(this.weeklyTime.value)}`, type: 'success' };
      return { text: 'Weekly — select at least one day', type: 'info' };
    }

    if (this.activeTab === 'event') {
      if (this.selectedTrigger)
        return { text: `⚡ Trigger: ${this.selectedTrigger.name}`, type: 'success' };
      return { text: '⚡ Select a trigger event to continue', type: 'warn' };
    }

    return { text: '', type: 'info' };
  }

  private fmt12(t: string): string {
    const [h, m] = t.split(':').map(Number);
    return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
  }

  private getCurrentDateTime(): { today: string; currentTime: string } {
    const now = new Date();
    const eatNow = new Date(now.getTime() + (3 * 60 * 60 * 1000));
    return {
      today: eatNow.toISOString().split('T')[0],
      currentTime: eatNow.toISOString().split('T')[1].slice(0, 5)
    };
  }


  // ─── Reset ─────────────────────────────────────────────

  onSubmit(): void {
    this.submitted = true;
    if (
      !this.title.value?.trim() ||
      !this.type.value ||
      !this.audience.value ||
      !this.module.value ||
      !this.message.value
    ) {
      return this.notificationForm.markAllAsTouched();
    }

    if (this.activeTab === 'WEEKLY_RECURRING') {

      this.weeklyTime.markAsTouched();

      if (!this.weeklyTime.value) {
        this.tosterService.error('Please select time');
        return;
      }

      if (!this.weeklyDate.value) {
        this.tosterService.error('Please select date');
        return;
      }

      if (this.selectedDays.length === 0) {
        this.daysError = true;
        this.tosterService.error('Please select at least one day');
        return;
      }
    }

    if (this.activeTab === 'ONE_TIME') {

      if (!this.oneTimeDate.value || !this.oneTimeTime.value) {

        this.oneTimeDate.markAsTouched();
        this.oneTimeTime.markAsTouched();

        this.tosterService.error('Please select date and time');
        return;
      }
    }

    const selectedDate = this.oneTimeDate.value;
    const selectedTime = this.oneTimeTime.value;

    const { today, currentTime } = this.getCurrentDateTime();

    if (selectedDate && selectedDate < today) {
      this.tosterService.error('Past date is not allowed');
      return;
    }

    if (selectedDate === today && selectedTime < currentTime) {
      this.tosterService.error('Past time is not allowed');
      return;
    }

    let utcDate = '';
    let utcTime = '';

    if (this.activeTab === 'ONE_TIME') {
      const localDateTime = new Date(
        `${this.oneTimeDate.value}T${this.oneTimeTime.value}:00`
      );
      const utcISOString = localDateTime.toISOString();
      utcDate = utcISOString.split('T')[0];
      utcTime = utcISOString.split('T')[1].slice(0, 8);
    } else {
      const localDateTime = new Date(
        `${this.weeklyDate.value}T${this.weeklyTime.value}:00`
      );
      const utcISOString = localDateTime.toISOString();
      utcDate = utcISOString.split('T')[0];
      utcTime = utcISOString.split('T')[1].slice(0, 8);
    }

    // ─── Payload ──────────────────────────────────────────

    const payload: any = {
      moduleId: Number(this.module.value),
      moduleSubId: Number(this.subModule.value),
      messageBody: this.message.value,
      scheduleType:
        this.activeTab === 'ONE_TIME'
          ? 'ONE_TIME'
          : 'WEEKLY_RECURRING',
      targetAudience: this.audience.value,
      title: this.title.value,
      type: this.type.value,
      scheduleConfig:
        this.activeTab === 'ONE_TIME'
          ? {
            date: utcDate,
            time: utcTime
          }
          : {
            days: this.selectedDays.map(d => d.toLowerCase()),
            date: utcDate,
            time: utcTime
          }
    };
    if (this.editMode) {
      payload.notificationId = this.notificationId;
    }
    const formData = new FormData();
    formData.append('data', JSON.stringify(payload));
    if (this.mediaFile) {
      formData.append('image', this.mediaFile);
    }
    if (this.excelFile) {
      formData.append('excel', this.excelFile);
    }
    console.log('Payload:', payload);
    this.editMode
      ? this.updateScheduleNotification(formData)
      : this.scheduleNotification(formData);
  }

  resetForm(): void {
    this.submitted = false;
    this.selectedDays = [];
    this.selectedTrigger = null;
    this.mediaFile = null;
    this.daysError = false;
    this.triggerError = false;
    this.notificationForm.reset({ oneTimeTime: '10:00', weeklyTime: '10:00' });
  }

  cancelForm(): void {
    if (confirm('Discard this notification?')) {
      this.resetForm();
    }
  }

  getTagsData() {
    this.authService.getTagData().subscribe({
      next: (res: any) => {
        if (res) {
          this.tagsData = res;
        }
      },
      error: (error: any) => {
        console.log(error);
      }
    })
  }

  async onFileSelect(event: any) {
    const fileInput = event.target;
    if (!fileInput.files || fileInput.files.length === 0) {
      return;
    }
    const file = fileInput.files[0];
    if (file?.name) {
      const exten = file.name.split('.').pop()?.toLowerCase();
      if (exten !== 'xls' && exten !== 'xlsx') {
        this.tosterService.error("Please upload a valid excel file");
        return;
      }
      const isValid = await this.validateExcelRowLimit(file);
      if (!isValid) {
        fileInput.value = '';
        return;
      }
      this.tosterService.success("File uploaded succesfully")
    }
    if (file) {
      this.excelFile = file;
    }
  }

  validateExcelRowLimit(file: File): Promise<boolean> {
    this.ExcelFileName = file?.name || ''
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const workbook = XLSX.read(e.target.result, { type: 'binary' });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];

        const allRows = XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][];
        if (allRows.length <= 1) {
          this.tosterService.error("File is empty. Please upload a file with at least one record");
          resolve(false);
          this.excelFile = null;
          this.ExcelFileName = '';
          return;
        }

        // header validation
        const data: any = XLSX.utils.sheet_to_json(sheet, { header: 1 })[0]
        const fromattedData = data.toString();
        console.log(fromattedData)
        if (fromattedData !== 'MSISDN') {
          this.tosterService.error("Cannot upload file with wrong format")
          resolve(false);
          this.excelFile = null;
          this.ExcelFileName = '';
          return;
        }
        // header validation

        // validation for only number in sheet except header
        const arr: any = XLSX.utils.sheet_to_json(sheet, { header: 1 }).flat();
        for (let i = 1; i < arr.length; i++) {
          const value = arr[i];
          if (!/^\d+$/.test(value.toString())) {           //  check only digits
            this.tosterService.error(`Invalid data at row ${i + 1}. Only numbers allowed`);
            resolve(false);
            this.excelFile = null;
            this.ExcelFileName = '';
            return;
          }

          const strValue = value.toString();
          if (strValue.length < 9 || strValue.length > 12) {
            this.tosterService.error(`Invalid data at row ${i + 1}.Please check number length`);
            resolve(false);
            this.excelFile = null;
            this.ExcelFileName = '';
            return;
          }
        }
        // validation for only number in sheet except header

        const jsonData = XLSX.utils.sheet_to_json(sheet);
        const rowCount = jsonData.length;

        if (rowCount > 100000) {
          this.tosterService.error("File should not contain more than 1 lakh records");
          resolve(false);
          this.excelFile = null;
          this.ExcelFileName = '';
        } else {
          resolve(true);
        }
      };

      reader.onerror = () => {
        this.tosterService.error("Error reading file");
        reject(false);
      };
      reader.readAsBinaryString(file);
    });
  }

  onModuleChange(event: any, text: String) {
    const selectedValue = event.target.value;
    if (text == 'playlist') {
      selectedValue == '3' ? this.showPlayistDiv = true : this.showPlayistDiv = false
      selectedValue == '2' ? this.showGenreDiv = true : this.showGenreDiv = false
    }
    if (text == 'custom') {
      if (selectedValue == 'CUSTOM_UPLOAD') {
        this.showCustomUploadDiv = true
      } else {
        this.showCustomUploadDiv = false
      }
    }
  }

  downloadSampleExcel() {
    this.spinner.show()
    this.authService.downloadSample().subscribe({
      next: (res: Blob) => {
        if (res) {
          const blob = new Blob([res], { type: 'application/octet-stream' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a')
          a.href = url
          a.download = 'sample_donwload.xlsx';
          a.click()
          window.URL.revokeObjectURL(url)
          this.tosterService.success('File Downloaded succesfully');
        }
      },
      error: (error: any) => {
        console.log(error)
        this.tosterService.error(error?.err?.message || 'Something went wrong while downloading')
      }
    })
    this.spinner.hide()
  }

  patchFormsDetails() {
    this.spinner.show();
    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.editMode = true;
      this.authService.getNotificationById(id).subscribe({
        next: (res: any) => {
          const patchedData = res[0];
          this.notificationId = res[0]?.notificationId;
          if (patchedData) {
            this.notificationForm.patchValue({
              title: patchedData.title,
              type: patchedData.type,
              audience: patchedData.targetAudience,
              module: patchedData.moduleId,
              message: patchedData.messageBody,
            });
            if (patchedData.imageUrl) {
              this.imagePreviewUrl = patchedData.imageUrl;
            }

            if (patchedData?.moduleName === 'Popular Playlists') {
              this.showPlayistDiv = true;
              this.showGenreDiv = false;
              this.notificationForm.patchValue({
                subModule: patchedData.moduleSubId
              });
            }

            if (patchedData?.moduleName === 'Top Genre') {
              this.showPlayistDiv = false;
              this.showGenreDiv = true;
              this.notificationForm.patchValue({
                subModule: patchedData.moduleSubId
              });
            }


            // ─── Patch delivery schedule ───────────────────
            const schedule = patchedData.scheduleType;
            if (schedule === 'ONE_TIME') {
              this.activeTab = 'ONE_TIME';

              const utcDate = patchedData.scheduleConfig?.date;
              const utcTime = patchedData.scheduleConfig?.time;

              const utcDateTime = new Date(`${utcDate}T${utcTime}Z`);
              const year = utcDateTime.getFullYear();
              const month = String(utcDateTime.getMonth() + 1).padStart(2, '0');
              const day = String(utcDateTime.getDate()).padStart(2, '0');

              const localDate = `${year}-${month}-${day}`;

              // convert to local/system time
              const localTime = utcDateTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
              this.notificationForm.patchValue({ oneTimeDate: localDate, oneTimeTime: localTime, });

              // this.notificationForm.patchValue({
              //   oneTimeDate: patchedData.scheduleConfig?.date,
              //   oneTimeTime: patchedData.scheduleConfig?.time?.slice(0, 5),
              // });
            } else if (schedule === 'WEEKLY_RECURRING') {
              this.activeTab = 'WEEKLY_RECURRING';
              this.selectedDays = (patchedData.scheduleConfig?.days || []).map(
                (d: string) => d.charAt(0).toUpperCase() + d.slice(1)
              );
              this.notificationForm.patchValue({
                weeklyTime: patchedData.scheduleConfig?.time?.slice(0, 5),
              });
            }
            this.notificationForm.get('audience')?.disable();
            this.notificationForm.get('type')?.disable();
          }
          this.spinner.hide();
        },
        error: (err) => {
          console.log(err);
          this.spinner.hide();
        }
      });
    } else {
      this.spinner.hide();
    }
  }

  removeImage(): void {
    this.imagePreviewUrl = null;
    this.mediaFile = null;
  }

  scheduleNotification(payload: any) {
    this.spinner.show()
    if (payload) {
      this.authService.scheduleNotification(payload).subscribe({
        next: (res: any) => {
          this.spinner.hide();
          this.tosterService.success(res?.message || 'Notification scheduled succesfully')
          this.notificationForm.reset();
          let params: any = { page: this.page, size: this.size };
          this.router.navigate(['/dashboard']).then(() => {
            this.getAllNotifications(params)
          })
        },
        error: (error: any) => {
          this.spinner.hide();
          console.log(error);
          this.tosterService.error(error?.message || 'Something went wrong')
        }
      })
    }
  }

  updateScheduleNotification(data: any) {
    this.spinner.show()
    this.authService.updateScheduleNotification(data).subscribe({
      next: (res: any) => {
        this.spinner.hide()
        this.tosterService.success(res?.message || 'Notification updated succesfully')
        let params: any = { page: this.page, size: this.size };
        this.router.navigate(['/dashboard']).then(() => {
          this.getAllNotifications(params)
        })
      },
      error: (error: any) => {
        console.log(error);
        this.spinner.hide()
        this.tosterService.error(error?.message || 'Something went wrong')
      }
    })
  }

  back() {
    this.router.navigate(['/dashboard'], { state: { fetch: true } })
  }

  wordLimitValidator(event: any, module: string) {
    const value = event.target.value;
    const characterCount = value?.length;
    if (module == 'title') {
      if (characterCount > 200) {
        event.target.value = value.substring(0, 200);
      }
    }
    if (module == 'message') {
      if (characterCount > 500) {
        event.target.value = value.substring(0, 500);
      }
    }
  }

  handlePaste(event: any) {
    event?.preventDefault()
  }

  get filteredModules() {
    const audience = this.notificationForm.get('audience')?.value;
    if (audience === 'UNREGISTERED_USERS') {
      return this.tagsData?.notificationModules?.filter(
        (m: any) =>
          m.screenCode !== 'SUBSCRIPTION_PLANS' &&
          m.screenCode !== 'PAYMENT_TOP_UP'
      );
    }
    return this.tagsData?.notificationModules;
  }

  getAllNotifications(params: any) {
    if (params) {
      this.authService.getNotifications(params).subscribe({
        next: (res: any) => {
          this.notificationData = res?.content ?? [];
          this.currentPage = res.page + 1;
          this.totalEntries = res.totalElements;
          this.totalPages = res.totalPages;
          this.sharedService.triggerOpenManageNotification();
        },
        error: (err) => {
          console.error(err)
        }
      });
    }
  }

}
